/**
 * Headless Uppy upload — drives a presigned direct-to-S3 transfer for a single
 * file, then calls the backend /finalize so the document is registered.
 *
 * We keep the existing upload UI (the react-dropzone wizard); Uppy is used purely
 * as the transfer engine. Its AwsS3 hooks call our authenticated presign endpoints
 * (via the axios `api` client), and Uppy performs the actual PUTs straight to
 * S3/MinIO using the returned presigned URLs (no auth header on those).
 */

import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import axios, { AxiosProgressEvent } from 'axios';
import { documentsApi, CHUNKED_UPLOAD_THRESHOLD, type DocumentUploadResponse } from './api';
import { normalizeErrorDetail } from './apiErrors';

/**
 * Thrown when the backend reports presigned uploads are unavailable (HTTP 409 —
 * feature disabled or storage backend not S3-compatible). Callers catch this to
 * fall back to the proxied upload path.
 */
export class PresignUnavailableError extends Error {
  constructor(message = 'Presigned uploads are not available') {
    super(message);
    this.name = 'PresignUnavailableError';
  }
}

function isConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

export interface UppyUploadOptions {
  caseId?: string;
  metadata?: Record<string, unknown>;
  onProgress?: (percent: number) => void;
}

/**
 * Upload one file directly to S3/MinIO via presigned URLs, then finalize it.
 *
 * @throws {PresignUnavailableError} when the backend has presigning disabled.
 * @throws the underlying error for any other failure.
 */
export async function uploadFileViaPresignedS3(
  file: File,
  options: UppyUploadOptions = {}
): Promise<DocumentUploadResponse> {
  const { caseId, metadata, onProgress } = options;
  const contentType = file.type || 'application/octet-stream';

  // Captured from the presign hooks so we know which key to finalize afterwards.
  let storageKey: string | null = null;
  let presignUnavailable = false;

  const uppy = new Uppy({ autoProceed: false, allowMultipleUploadBatches: false });

  // Wrap a presign call so a 409 is surfaced as PresignUnavailableError after the
  // upload settles (Uppy turns thrown hook errors into a failed upload result).
  const guard = async <T>(fn: () => Promise<T>): Promise<T> => {
    try {
      return await fn();
    } catch (error) {
      if (isConflict(error)) {
        presignUnavailable = true;
        throw new PresignUnavailableError(
          axios.isAxiosError(error)
            ? normalizeErrorDetail(error.response?.data?.detail)
            : undefined
        );
      }
      throw error;
    }
  };

  uppy.use(AwsS3, {
    shouldUseMultipart: (f) => (f.size ?? 0) > CHUNKED_UPLOAD_THRESHOLD,

    // Small files: a single presigned PUT.
    getUploadParameters: (f) =>
      guard(async () => {
        const res = await documentsApi.presignSign({
          filename: f.name ?? file.name,
          content_type: f.type || contentType,
          size: f.size ?? file.size,
          case_id: caseId,
        });
        storageKey = res.key;
        return { method: 'PUT' as const, url: res.url, fields: {}, headers: res.headers };
      }),

    // Large files: multipart.
    createMultipartUpload: (f) =>
      guard(async () => {
        const res = await documentsApi.presignCreateMultipart({
          filename: f.name ?? file.name,
          content_type: f.type || contentType,
          size: f.size ?? file.size,
          case_id: caseId,
        });
        storageKey = res.key;
        return { uploadId: res.uploadId, key: res.key };
      }),

    signPart: (_f, { key, uploadId, partNumber }) =>
      guard(async () => documentsApi.presignSignPart(key!, uploadId!, partNumber)),

    listParts: (_f, { key, uploadId }) =>
      guard(async () => documentsApi.presignListParts(key!, uploadId!)),

    completeMultipartUpload: (_f, { key, uploadId, parts }) =>
      guard(async () =>
        documentsApi.presignCompleteMultipart(
          key!,
          uploadId!,
          (parts as Array<{ PartNumber: number; ETag: string }>).map((p) => ({
            PartNumber: p.PartNumber,
            ETag: p.ETag,
          }))
        )
      ),

    abortMultipartUpload: (_f, { key, uploadId }) =>
      guard(async () => {
        await documentsApi.presignAbortMultipart(key!, uploadId!);
      }),
  });

  if (onProgress) {
    uppy.on('upload-progress', (_file, progress) => {
      if (progress.bytesTotal) {
        onProgress(Math.round((progress.bytesUploaded / progress.bytesTotal) * 100));
      }
    });
  }

  try {
    uppy.addFile({ name: file.name, type: contentType, data: file });
    const result = await uppy.upload();

    if (result?.failed?.length) {
      if (presignUnavailable) {
        throw new PresignUnavailableError();
      }
      throw result.failed[0].error ?? new Error('Upload failed');
    }

    if (!storageKey) {
      throw new Error('Upload completed without a storage key');
    }

    const document = await documentsApi.finalizeUpload({
      key: storageKey,
      filename: file.name,
      content_type: contentType,
      size: file.size,
      case_id: caseId,
      metadata,
    });

    onProgress?.(100);
    return document;
  } finally {
    uppy.destroy();
  }
}

/**
 * Upload one file to a case, preferring the presigned direct-to-S3 path and
 * falling back to the proxied upload (chunked for large files) when presigning
 * is unavailable. This is THE single upload path — shared by the create-case /
 * add-documents wizard and the documents-sidebar drop zone — so both flows get
 * identical behaviour without a second uploader.
 */
export async function uploadDocumentWithFallback(
  file: File,
  options: UppyUploadOptions = {}
): Promise<DocumentUploadResponse> {
  const { caseId, metadata, onProgress } = options;

  const proxiedUpload = () =>
    file.size > CHUNKED_UPLOAD_THRESHOLD
      ? documentsApi.uploadFileChunked(file, { case_id: caseId, metadata, onProgress })
      : documentsApi.uploadFile(file, {
          case_id: caseId,
          metadata,
          onUploadProgress: (progressEvent: AxiosProgressEvent) =>
            onProgress?.(
              Math.round((progressEvent.loaded * 100) / (progressEvent.total ?? progressEvent.loaded))
            ),
        });

  try {
    return await uploadFileViaPresignedS3(file, { caseId, metadata, onProgress });
  } catch (err) {
    if (err instanceof PresignUnavailableError) {
      return proxiedUpload();
    }
    throw err;
  }
}
